import React, { useCallback, useMemo, useRef, useState } from 'react';
import styles from './ColorPicker.module.scss';
import { HexColorInput, HexAlphaColorPicker } from 'react-colorful';
import { CustomButton } from '../Button/Button';
import { useTranslation } from 'react-i18next';
import ColorPickerImage from '../../assets/colorPicker.png';
import useOutsideColorPickerClick from '../../hooks/useOutsideColorPickerClick';
import cx from 'classnames';

const ColorPicker = ({
  title,
  currentColor,
  onChoose,
  isShowCurrentColor,
  className,
  pickerClassName,
  colorImageClassName,
  parentRef = null,
}) => {
  const [isColorPickerActive, setIsColorPickerActive] = useState(false);
  const initialColor = useMemo(() => currentColor, [isColorPickerActive]);
  const [pickerModalStyles, setPickerModalStyles] = useState({});

  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);

  const colorPickerRef = useRef(null);
  const colorContainerRef = useRef(null);

  const { t } = useTranslation();

  const handleCancel = () => {
    onChoose(initialColor);
    setIsColorPickerActive(false);
  };

  const handleChoose = () => {
    setIsColorPickerActive(false);
  };

  const handleOpenPicker = useCallback(() => {
    setIsColorPickerActive(true);
    setPickerModalStyles({ opacity: 0 });
    const styles = {};
    setTimeout(() => {
      if (isMobileView) {
        styles.bottom = `${colorContainerRef.current.offsetHeight + 10}px`;
      } else {
        styles.left = colorContainerRef.current?.offsetWidth + 10;
        // If parent specified, place the color picker within it
        if (parentRef) {
          const { left, width } = parentRef.current.getBoundingClientRect();
          const { left: containerLeft } = colorContainerRef.current.getBoundingClientRect();
          if (containerLeft + styles.left + colorPickerRef.current.offsetWidth > left + width) {
            styles.left = -colorPickerRef.current.offsetWidth - 10;
          }
        }
        styles.left = `${styles.left}px`;
      }
      setPickerModalStyles(styles);
    }, 100);
  }, [parentRef, isMobileView]);

  useOutsideColorPickerClick(colorPickerRef, () => {
    if (isColorPickerActive) {
      handleCancel();
    }
  });

  return (
    <div className={styles.container}>
      {title && <div className={styles.title}>{title}</div>}
      <div className={cx(styles.colorContainer, className)} onClick={handleOpenPicker} ref={colorContainerRef}>
        <div className={styles.itemContainer}>
          <span>{currentColor}</span>
        </div>
        <div className={cx(styles.colorImageContainer, colorImageClassName)}>
          {isShowCurrentColor ? (
            <div className={styles.colorImage} style={{ backgroundColor: currentColor }} />
          ) : (
            <img src={ColorPickerImage} className={styles.colorImage} alt={t('accessibility.color_picker')} />
          )}
        </div>
      </div>
      {isColorPickerActive && (
        <div ref={colorPickerRef} className={cx(styles.hexColorPicker, pickerClassName)} style={pickerModalStyles}>
          <HexAlphaColorPicker color={currentColor || ''} onChange={onChoose} />
          <HexColorInput alpha={true} prefixed={true} color={currentColor || ''} onChange={onChoose} />
          <div className={styles.btnContainer}>
            <CustomButton label={t('general.cancel')} variant="secondary" onClick={handleCancel} className={styles.cancelButton} />
            <CustomButton label={t('general.choose')} variant="primary" onClick={handleChoose} className={styles.cancelButton} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
